import { el, mondayOf } from './ui'

// A job count for anyone who doesn't see prices (Liam, 14 Oct 2026, approved design: "a job count Sendi sees go up
// instead of money"). At the top of their Today, in the player card's slate and lemon: today's jobs done in a ring out
// of the day's, how many to go, and this week, this month and their best day, from the days the server counted for
// them (GET /api/today done_days). Each Done ticks it up with a "+1". Owners keep "£ taken" as before.

export interface JobCountFacts { done: number; total: number; week: number; month: number; best: number }

// done_days: [date, jobs done that day] over the last year, today's included.
export function jobCountFacts(today: string, done: number, total: number, doneDays: [string, number][]): JobCountFacts {
  const monday = mondayOf(today), first = `${today.slice(0, 7)}-01`
  // Today's own count is the list on screen (it moves at once with a Done); the other days are the server's.
  const days = new Map(doneDays)
  days.set(today, done)
  let week = 0, month = 0, best = 0
  for (const [day, n] of days) {
    if (day > today) continue
    if (day >= monday) week += n
    if (day >= first) month += n
    best = Math.max(best, n)
  }
  return { done, total, week, month, best }
}

const R = 42, C = 2 * Math.PI * R

export function jobCountCard(f: JobCountFacts, extra: string, plusOne: boolean): HTMLElement {
  const ns = 'http://www.w3.org/2000/svg'
  const svg = document.createElementNS(ns, 'svg')
  svg.setAttribute('viewBox', '0 0 104 104'); svg.setAttribute('class', 'job-count-ring'); svg.setAttribute('aria-hidden', 'true')
  const circle = (stroke: string, dash: string | null): SVGCircleElement => {
    const c = document.createElementNS(ns, 'circle')
    c.setAttribute('cx', '52'); c.setAttribute('cy', '52'); c.setAttribute('r', String(R)); c.setAttribute('fill', 'none')
    c.setAttribute('stroke', stroke); c.setAttribute('stroke-width', '10')
    if (dash) { c.setAttribute('stroke-dasharray', dash); c.setAttribute('stroke-linecap', 'round'); c.setAttribute('transform', 'rotate(-90 52 52)') }
    return c
  }
  svg.append(circle('#46525f', null))
  if (f.done > 0 && f.total > 0) svg.append(circle('#f4dc52', `${(C * Math.min(1, f.done / f.total)).toFixed(1)} ${C.toFixed(1)}`))
  const left = Math.max(0, f.total - f.done)
  const ring = el('div', { class: 'job-count-ring-box' }, svg, el('span', { class: 'job-count-ring-n' }, String(f.done)), el('span', { class: 'job-count-ring-of' }, `of ${f.total}`))
  const words = el('div', { class: 'job-count-words' },
    el('span', { class: 'job-count-kicker' }, 'Today'),
    el('strong', { class: 'job-count-done' }, `${f.done} ${f.done === 1 ? 'job' : 'jobs'} done`),
    el('span', { class: 'job-count-left' }, [left ? `${left} to go` : f.total ? 'All done' : '', extra].filter(Boolean).join(' · ')))
  const head = el('div', { class: 'job-count-head' }, ring, words)
  if (plusOne) head.append(el('span', { class: 'job-count-plus', 'aria-hidden': 'true' }, '+1'))
  const stat = (n: number, label: string): HTMLElement => el('div', { class: 'job-count-stat' }, el('span', { class: 'job-count-stat-n' }, String(n)), el('span', { class: 'job-count-stat-label' }, label))
  return el('section', { class: 'job-count-card', 'aria-label': `${f.done} of ${f.total} jobs done today; ${f.week} this week, ${f.month} this month, best day ${f.best}`, 'aria-live': 'polite' },
    head, el('div', { class: 'job-count-stats' }, stat(f.week, 'This week'), stat(f.month, 'This month'), stat(f.best, 'Best day')))
}
